import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, getDefaultRedirect } from '@/hooks/useAuth';
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat';
import { LoginPage } from '@/modules/auth/LoginPage';
import { ForgotPasswordPage } from '@/modules/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/modules/auth/ResetPasswordPage';
import { RegisterPage } from '@/modules/auth/RegisterPage';
import { AcademyRegisterPage } from '@/modules/auth/AcademyRegisterPage';
import { RegistrationStatusPage } from '@/modules/auth/RegistrationStatusPage';
import { JoinPage } from '@/modules/auth/JoinPage';
import { OAuthCallbackPage } from '@/pages/OAuthCallbackPage';
import { LtiLaunchPage } from '@/pages/LtiLaunchPage';
import { LtiDeepLinkPage } from '@/pages/LtiDeepLinkPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { TermsPage } from '@/pages/TermsPage';
import { TestLinkPage } from '@/modules/student/pages/TestLinkPage';
import { lazy, Suspense, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

// Lazy-loaded module entry points
const StudentModule = lazy(() => import('@/modules/student/StudentModule'));
const OrganizerModule = lazy(() => import('@/modules/organizer/OrganizerModule'));
const AdminModule = lazy(() => import('@/modules/admin/AdminModule'));
const GraderModule = lazy(() => import('@/modules/grader/GraderModule'));
const SupportEngineerModule = lazy(() => import('@/modules/support-engineer/SupportEngineerModule'));
const SupportLeadModule = lazy(() => import('@/modules/support-lead/SupportLeadModule'));
const AcademyAdminModule = lazy(() => import('@/modules/academy-admin/AcademyAdminModule'));

// ============================================================================
// Loading Fallback
// ============================================================================

function PageLoader() {
  return (
    <div className="page-loader">
      <Loader2 size={32} className="page-loader__spinner" />
      <p>Loading...</p>
    </div>
  );
}

// ============================================================================
// ProtectedRoute
// ============================================================================

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole: string;
}

function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated || !user) {
    const redirect = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  if (!user.roles.includes(requiredRole)) {
    // Redirect to the user's default page if they lack the required role
    return <Navigate to={getDefaultRedirect(user.roles)} replace />;
  }

  return <>{children}</>;
}

// ============================================================================
// Root Redirect
// ============================================================================

function RootRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={getDefaultRedirect(user.roles)} replace />;
}

// ============================================================================
// App
// ============================================================================

// Pings the session heartbeat endpoint while the user is authed. Lives inside
// BrowserRouter because useAuth depends on it.
function SessionHeartbeat() {
  const { isAuthenticated } = useAuth();
  useSessionHeartbeat(isAuthenticated);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter basename="/assess">
        <SessionHeartbeat />
        <ImpersonationBanner />
        <Toaster position="top-right" richColors />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/academy" element={<AcademyRegisterPage />} />
          <Route path="/registration/status" element={<RegistrationStatusPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/auth/oauth/callback" element={<OAuthCallbackPage />} />
          <Route path="/lti/launch" element={<LtiLaunchPage />} />
          <Route path="/lti/deep-link" element={<LtiDeepLinkPage />} />
          <Route path="/t/:code" element={<TestLinkPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Role-protected routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizer/*"
            element={
              <ProtectedRoute requiredRole="organizer">
                <OrganizerModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grader/*"
            element={
              <ProtectedRoute requiredRole="grader">
                <GraderModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/*"
            element={
              <ProtectedRoute requiredRole="student">
                <StudentModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support-engineer/*"
            element={
              <ProtectedRoute requiredRole="support_engineer">
                <SupportEngineerModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support-lead/*"
            element={
              <ProtectedRoute requiredRole="support_lead">
                <SupportLeadModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/academy-admin/*"
            element={
              <ProtectedRoute requiredRole="academy_admin">
                <AcademyAdminModule />
              </ProtectedRoute>
            }
          />

          {/* Root redirect */}
          <Route path="/" element={<RootRedirect />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
